import { X, AlertTriangle } from 'lucide-react';
import { FormField } from '@/features/application/types/forms';

interface DuplicateMappingWarningModalProps {
  isOpen: boolean;
  onClose: () => void;
  newFieldLabel: string;
  conflictingFields: FormField[];
  crmFieldLabel: string;
}

/**
 * Modal component that warns users about duplicate field mappings
 */
const DuplicateMappingWarningModal = ({
  isOpen,
  onClose,
  newFieldLabel,
  conflictingFields,
  crmFieldLabel,
}: DuplicateMappingWarningModalProps) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 max-w-2xl w-full mx-4 max-h-[80vh] overflow-hidden flex flex-col">
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center text-red-600">
            <AlertTriangle className="h-6 w-6 mr-2" />
            <h2 className="text-xl font-bold text-slate-900">Mapping Not Allowed</h2>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="mb-6">
          <p className="text-slate-700 mb-4">
            The field <span className="font-semibold">"{newFieldLabel}"</span> cannot be mapped to{' '}
            <span className="font-semibold">{crmFieldLabel}</span> because{' '}
            <span className="font-semibold">{conflictingFields.length}</span> other field
            {conflictingFields.length === 1 ? ' is' : 's are'} already mapped to the same CRM
            location.
          </p>
          <p className="text-slate-500 text-sm">
            Each CRM field location can only receive data from one form field. Please choose a
            different CRM field or clear the existing mapping manually first.
          </p>
        </div>

        {/* Scrollable content area */}
        <div className="flex-1 overflow-y-auto">
          <div className="space-y-4">
            {conflictingFields.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold text-slate-800 mb-3">
                  Currently Mapped Field{conflictingFields.length === 1 ? '' : 's'} (
                  {conflictingFields.length})
                </h3>
                <div className="space-y-2">
                  {conflictingFields.map((field) => (
                    <div key={field.id} className="p-3 bg-red-50 border border-red-200 rounded-md">
                      <div>
                        <p className="font-medium text-slate-900">{field.label}</p>
                        <p className="text-sm text-slate-600">
                          Field ID: <span className="font-mono text-xs">{field.id}</span>
                        </p>
                        <p className="text-sm text-slate-600">
                          Mapped to: <span className="font-medium">{crmFieldLabel}</span>
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="mt-6 flex justify-end pt-4 border-t border-slate-200">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-slate-300 rounded-md text-slate-700 hover:bg-slate-50 transition-colors focus:outline-none focus:ring-2 focus:ring-slate-300"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default DuplicateMappingWarningModal;
